import { useState } from 'react'

interface SaveCalculationModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (name: string, notes: string) => Promise<void>
}

export function SaveCalculationModal({ isOpen, onClose, onSave }: SaveCalculationModalProps) {
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSave = async () => {
    if (!name.trim()) return
    
    setSaving(true)
    try {
      await onSave(name, notes)
      setName('')
      setNotes('')
      onClose()
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save calculation. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Save Calculation</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name (required)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My January 2025 Footprint"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={saving}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this calculation..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              disabled={saving}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface HistoryViewerProps {
  isOpen: boolean
  onClose: () => void
  calculations: any[]
  loading: boolean
  onDelete: (id: string) => Promise<void>
  onLoad: (calculation: any) => void
}

export function HistoryViewer({ isOpen, onClose, calculations, loading, onDelete, onLoad }: HistoryViewerProps) {
  const [deleting, setDeleting] = useState<string | null>(null)

  if (!isOpen) return null

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this calculation?')) return
    
    setDeleting(id)
    try {
      await onDelete(id)
    } catch (error) {
      alert('Failed to delete calculation.')
    } finally {
      setDeleting(null)
    }
  }

  const handleLoad = (calculation: any) => {
    onLoad(calculation)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Saved Calculations</h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : calculations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No saved calculations yet. Save your first calculation to see it here!
            </div>
          ) : (
            <div className="space-y-3">
              {calculations.map((calc) => (
                <div key={calc._id} className="border border-gray-200 rounded-lg p-4 hover:border-green-500 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{calc.userName || 'Unnamed Calculation'}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Total: {calc.totalEmissions.toFixed(2)} kg CO₂e
                      </p>
                      {calc.notes && (
                        <p className="text-sm text-gray-500 mt-1">{calc.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(calc.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleLoad(calc)}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => handleDelete(calc._id)}
                        disabled={deleting === calc._id}
                        className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {deleting === calc._id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}